/**
 * The far island of «Один блок»: a dragon, a stair, a lava room, a portal.
 *
 * "Додай до мапи «Один блок» новий острів, до якого гравцю треба буде
 * добратись. На острові буде лігво дракона, біля лігва - сходи, які ведуть
 * під лігво, і там кімната з підлогою із лави. В кінці кімнати стоїть
 * непрацюючий портал." Then: "збільши острів втричі і нехай виглядає ніби
 * вирваний шматок світу, а не квадрат; додай траву та дерева" - and
 * "віддали острів на 200 блоків".
 *
 * Everything here is arithmetic off the one block's position, so the page
 * and the server grow the same island cell for cell without a word passing
 * between them - the same reason the block's own drops are hashed rather
 * than rolled. The island lies a hundred and fifty blocks east of the block
 * at the block's own height, so a bridge of whatever fell out of it reaches
 * level. A hundred and fifty cells of void is the journey - it was two
 * hundred, and "it is hard to see the dragon island" from there.
 *
 * **A torn-out piece of country, not a square.** The outline is a circle
 * with lobes - the radius wobbles round it - and the underside is a lump:
 * deepest under the middle, ragged and shallow at the edges, as if the
 * ground had been ripped up by the roots. On top, grass, wild wheat and
 * flax, and a stand of trees.
 *
 * Reading it as a player meets it:
 *
 *   - a mound in the middle with the Змій's hall hollowed out inside, its
 *     mouth open toward the block so that whoever bridges across walks in
 *     facing him;
 *   - beside the mouth, a flight of stone stairs cut down into the island;
 *   - under the hall, a room whose floor is lava with a one-block stone rim
 *     round it, which is where the stairs land and how the far end is
 *     reached at all - by building across, which is this map's whole idea;
 *   - in the far wall, a frame of незламний блок with nothing in it. A
 *     portal that does not work yet. The day it does, its inside gets a
 *     block of its own.
 *
 * Pure: positions in, block ids out. No world, no chunks. The trees are the
 * one thing the world grows itself, from the spots named here.
 */
import { BlockId, stoneStairsId, LAVA } from "../block/registry.js";
import { oneBlockAt } from "./oneBlock.js";

/** How far east of the block the island's edge begins: the bridge's length. */
export const ISLAND_GAP = 150;
/** The island's mean radius, and how deep the lump goes under its middle. */
export const ISLAND_RADIUS = 15;
/**
 * Out to here the lump is full depth, so the rooms have solid country
 * round them. Less than the outline's lowest dip (about ten and a half),
 * or the shore would be a cliff to the bottom where the outline comes in.
 */
const CORE_RADIUS = 10;
export const ISLAND_DEPTH = 12;

export interface Landmarks {
  /** The island's bounding box, inclusive. Everything is inside it. */
  readonly box: { readonly x0: number; readonly x1: number; readonly z0: number;
    readonly z1: number; readonly y0: number; readonly y1: number };
  /** Its middle, at ground level. */
  readonly centre: { readonly x: number; readonly y: number; readonly z: number };
  /** The hall's floor, where the Змій is at home. */
  readonly lair: { readonly x: number; readonly y: number; readonly z: number };
  /** The top step of the stair, at ground level, and the bottom one. */
  readonly stairTop: { readonly x: number; readonly y: number; readonly z: number };
  readonly stairBottom: { readonly x: number; readonly y: number; readonly z: number };
  /** The middle of the lava floor. */
  readonly lava: { readonly x: number; readonly y: number; readonly z: number };
  /** The empty middle of the portal frame. */
  readonly portal: { readonly x: number; readonly y: number; readonly z: number };
}

/** Where everything is, for a world this many blocks across. */
export function oneIsland(span: number): Landmarks {
  const at = oneBlockAt(span);
  const cx = at.x + ISLAND_GAP + ISLAND_RADIUS + 2;
  const cz = at.z;
  const top = at.y;
  const reach = ISLAND_RADIUS + 4;
  return {
    box: { x0: cx - reach, x1: cx + reach, z0: cz - reach, z1: cz + reach,
      y0: top - ISLAND_DEPTH, y1: top + 12 },
    centre: { x: cx, y: top, z: cz },
    lair: { x: cx, y: top + 1, z: cz },
    stairTop: { x: cx - 9, y: top - 1, z: cz + 5 },
    stairBottom: { x: cx - 1, y: top - 9, z: cz + 5 },
    lava: { x: cx, y: top - 10, z: cz },
    portal: { x: cx + 6, y: top - 8, z: cz },
  };
}

/** A fixed hash, so both sides tear the same edge. Seed-free on purpose. */
function noise(a: number, b: number): number {
  let h = (a * 374761393 + b * 668265263 + 0x9e3779b9) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/**
 * The island's edge in this direction: a circle with lobes.
 *
 * Three waves of different counts, fixed phases, so the outline is one
 * particular ragged shape and not a regular flower. Between eleven and
 * fifteen and a half blocks out.
 */
function edgeRadius(theta: number): number {
  return ISLAND_RADIUS + 2.2 * Math.sin(3 * theta + 0.7) +
    1.4 * Math.sin(5 * theta + 2.1) + 0.9 * Math.sin(11 * theta + 4.0);
}

/** Is this column part of the island's footprint at all? */
function onIsland(l: Landmarks, x: number, z: number): boolean {
  const dx = x - l.centre.x, dz = z - l.centre.z;
  return Math.hypot(dx, dz) <= edgeRadius(Math.atan2(dz, dx));
}

/**
 * How far below the top this column is solid: the torn underside.
 *
 * Full depth out to where the rooms are, then falling away toward the edge
 * with a jitter per column, so the bottom reads as ripped rather than cut.
 */
function depthAt(l: Landmarks, x: number, z: number): number {
  const dx = x - l.centre.x, dz = z - l.centre.z;
  const d = Math.hypot(dx, dz);
  const r = edgeRadius(Math.atan2(dz, dx));
  if (d <= CORE_RADIUS) return ISLAND_DEPTH;
  const toEdge = Math.max(0, (r - d) / Math.max(1, r - CORE_RADIUS));
  return 2 + Math.floor(toEdge * (ISLAND_DEPTH - 2)) + Math.floor(noise(x, z) * 2.5) - 1;
}

/**
 * What stands in one cell of the island, or null for nothing.
 *
 * Asked cell by cell so the generator can walk the box once. The order of
 * the tests is the order things were carved: ground first, then the mound
 * on it, then the hollows cut out of both, then what is set into the
 * hollows.
 */
export function oneIslandBlock(span: number, x: number, y: number, z: number): number | null {
  const l = oneIsland(span);
  const { x0, x1, z0, z1, y0, y1 } = l.box;
  if (x < x0 || x > x1 || z < z0 || z > z1 || y < y0 || y > y1) return null;
  const top = l.centre.y;
  const cx = l.centre.x, cz = l.centre.z;
  if (!onIsland(l, x, z)) return null;
  const ground = y <= top && y > top - depthAt(l, x, z);

  // The mound over the hall: the upper half of an egg on the island's top.
  const mound = y > top && inEgg(x - cx, y - top, z - cz, 8, 9, 8);
  // The hall inside it, kept off the island's top so the floor is whole.
  const hall = y > top && inEgg(x - cx, y - (top + 2), z - cz, 4.2, 3.2, 5.2);
  // Its mouth, bored west toward the block: three wide, three tall.
  const mouth = y >= top + 1 && y <= top + 3 && z >= cz - 1 && z <= cz + 1 &&
    x >= cx - 9 && x <= cx;
  // The room under the hall, and the lava floor with its stone rim.
  const inRoomXZ = x >= cx - 5 && x <= cx + 5 && z >= cz - 5 && z <= cz + 5;
  const room = inRoomXZ && y >= top - 9 && y <= top - 6;
  const floor = inRoomXZ && y === top - 10;
  const rim = floor && (x === cx - 5 || x === cx + 5 || z === cz - 5 || z === cz + 5);
  // The stair, one step a cell, from the top step down to the rim.
  const sz = l.stairTop.z;
  const step = z === sz && x >= l.stairTop.x && x <= l.stairBottom.x
    ? l.stairTop.y - (x - l.stairTop.x) : null;
  const onStep = step !== null && y === step;
  const overStep = step !== null && y > step && y <= step + 3;
  // The portal frame, set into the far wall: a ring of незламний блок round
  // a three-by-three of nothing.
  const px = l.portal.x, py = l.portal.y, pz = l.portal.z;
  const inFrame = x === px && z >= pz - 2 && z <= pz + 2 && y >= py - 2 && y <= py + 2;
  const frameRing = inFrame && (z === pz - 2 || z === pz + 2 || y === py - 2 || y === py + 2);
  const frameHole = inFrame && !frameRing;
  // Wild wheat and flax on the grass, where nothing else stands.
  const plant = y === top + 1 && !mound && !mouth && step === null &&
    Math.hypot(x - cx, z - cz) > 3 && noise(x + 7, z + 3) < 0.14;

  if (frameRing) return BlockId.Bedrock;
  if (frameHole) return null;
  if (onStep) return stoneStairsId(3); // the tall half to the west: up is west
  if (overStep) return null;
  if (mouth || hall) return null;
  if (room) return null;
  if (rim) return BlockId.Stone;
  if (floor) return LAVA;
  if (y > top) {
    if (mound) return BlockId.Stone;
    if (plant) return noise(x, z + 11) < 0.6 ? BlockId.WildWheat : BlockId.WildFlax;
    return null;
  }
  if (!ground) return null;
  if (y === top) return BlockId.Grass;
  if (y === top - 1) return BlockId.Dirt;
  return BlockId.Stone;
}

/**
 * Where the trees stand: a stand of oak and acacia on the grass.
 *
 * Chosen on a loose grid with a jitter, kept off the mound, the mouth and
 * the stair, and off the edge, where a tree would hang over the void. The
 * world grows each one from here with its own tree-grower, so an island
 * oak is the same oak as a кремль one.
 */
export function oneIslandTrees(
  span: number,
): readonly { readonly x: number; readonly z: number; readonly kind: "oak" | "acacia" }[] {
  const l = oneIsland(span);
  const out: { x: number; z: number; kind: "oak" | "acacia" }[] = [];
  const cx = l.centre.x, cz = l.centre.z, top = l.centre.y;
  for (let gz = l.box.z0; gz <= l.box.z1; gz += 4) {
    for (let gx = l.box.x0; gx <= l.box.x1; gx += 4) {
      const x = gx + Math.floor(noise(gx, gz) * 3) - 1;
      const z = gz + Math.floor(noise(gz, gx) * 3) - 1;
      if (noise(x, z + 99) > 0.6) continue;
      const d = Math.hypot(x - cx, z - cz);
      if (d < 10 || d > edgeRadius(Math.atan2(z - cz, x - cx)) - 2) continue;
      if (z === l.stairTop.z || Math.abs(z - cz) <= 1) continue;
      if (oneIslandBlock(span, x, top, z) !== BlockId.Grass) continue;
      out.push({ x, z, kind: noise(x + 5, z) < 0.5 ? "oak" : "acacia" });
    }
  }
  return out;
}

function inEgg(dx: number, dy: number, dz: number, rx: number, ry: number, rz: number): boolean {
  const a = dx / rx, b = dy / ry, c = dz / rz;
  return a * a + b * b + c * c <= 1;
}
